import {
  ref,
  reactive,
  computed,
  onBeforeMount,
  getCurrentInstance,
  watch,
} from "@vue/composition-api";
import store from "@/store";
import router from "@/router";
import useModal from "@/@core/components/base-modal/hooks/useModal";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import usePagination from "@/views/components/pagination/usePagination";
import { privilegeCriteriaModel } from "@/models/privilege/privilege-form";
import useFlatPickr from "@/utils/hooks/useFlatPickr";

export default function usePrivilegeCriteria(props) {
  const {
    createForm,
    editForm,
    deleteItemList,
    fetchListData,
  } = privilegeCriteriaModel();
  const {
    pagination,
    paginationInfo,
    onPageChange,
    onFilterEntity,
  } = usePagination();
  const { openModal, closeModal } = useModal();
  const {
    openErrorModal,
    openSuccessModal,
    openWarningModal,
    openDefaultErrorModal,
  } = useValidationModal();
  const { resolveDate, resolveTime } = useFlatPickr();

  const vm = getCurrentInstance().proxy;

  const loading = reactive({
    tableField: false,
    tableData: false,
  });

  const formData = reactive({
    privilegeId: 0,
    critiriaTable: [],
    pointRef: 0,
  });

  const criteriaDetailItem = reactive({
    privilegeId: 0,
    selectId: 0,
    isNoDefault: false,
  });

  const fields = reactive({
    criteriaTable: [
      { key: "priority", label: "Priority" },
      { key: "name", label: "Name" },
      { key: "list", label: "List" },
      { key: "criteria", label: "Criteria" },
      { key: "updatedtime", label: "Lastest Update" },
      { key: "actions", label: "" },
    ],
  });

  const isEditCriteria = ref(false);

  const modalType = reactive({
    manageCriteriaList: false,
  });

  const pointMode = reactive({
    DEFAULT: 1,
    CUSTOM: 2,
  });

  const openCriteriaListModal = (selectId, isEdit = false) => {
    isEditCriteria.value = false;
    if (selectId) criteriaDetailItem.selectId = selectId;
    if (isEdit == true) {
      isEditCriteria.value = true;
    }
    criteriaDetailItem.privilegeId = formData.privilegeId;
    modalType.manageCriteriaList = true;
    openModal();
  };

  const openDeleteWarningModal = (itemData) => {
    openWarningModal(
      "ลบ Criteria",
      `ต้องการลบ ${itemData.name} หรือไม่`,
      () => {
        deleteCriteria(itemData.criteria_group_id);
      }
    );
  };

  const deleteCriteria = (id) => {
    window.$loading(true);
    deleteItemList.payload.Privilege_Id = formData.privilegeId; // required
    deleteItemList.payload.Criteria_Group_Id = id; // required
    deleteItemList.payload.Req_IdentityId = 1; // required

    store
      .dispatch("api/apiRequest", deleteItemList)
      .then((response) => {
        openSuccessModal(
          "ทำรายการสำเร็จ !",
          null,
          "ตกลง",
          () => {
            fetchDataList();
          },
          () => {
            fetchDataList();
          }
        );
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const closeManageCriteriaListModal = () => {
    modalType.manageCriteriaList = false;
    closeModal();
  };

  const saveCriteriaList = (inputData, isEditModal = false) => {
    window.$loading(true);
    const payload = {
      Privilege_Id: formData.privilegeId, // required
      Priority: parseInt(inputData.priority), // required
      Name: inputData.name, // required
      Note: inputData.note,
      Req_IdentityId: 1, // required
    };

    createForm.payload = payload;
    editForm.payload = payload;

    let payloadParam = {};

    if (isEditModal) {
      editForm.payload.Criteria_Group_Id = inputData.criteriaGroupId;
      editForm.payload.Status = 1;

      payloadParam = editForm;
    } else {
      createForm.payload.Point_Mode = inputData.pointMode; // required 1 : Default use Privilege.Point_Ref | 2 : Custome_Point
      createForm.payload.Type = inputData.criteriaType; // required
      createForm.payload.Point =
        inputData.pointMode == pointMode.DEFAULT
          ? formData.pointRef
          : parseInt(inputData.point); // required
      createForm.payload.IsVisible = inputData.isVisible; // required
      createForm.payload.IsIssuable = inputData.isIssuable; // required
      createForm.payload.IsRedeemable = inputData.isRedeemable; // required

      if (!criteriaDetailItem.isNoDefault) {
        createForm.payload.TagList =
          inputData.tag.length > 0
            ? inputData.tag.map((o, index) => ({
                Seq: index + 1,
                Tag: o.label,
                Condition: index == 0 ? "AND" : "OR",
                GroupLv: 1,
                GroupNum: 1,
              }))
            : [];

        createForm.payload.TierList =
          inputData.tier.length > 0
            ? inputData.tier.map((o, index) => ({
                Seq: index,
                Tag: o.label,
                Condition: index == 0 ? "AND" : "OR",
                GroupLv: 1,
                GroupNum: 1,
              }))
            : [];
      }

      payloadParam = createForm;
    }

    store
      .dispatch("api/apiRequest", payloadParam)
      .then((resp) => {
        modalType.manageCriteriaList = false;
        closeModal();
        fetchDataList();
      })
      .catch((err) => {
        openDefaultErrorModal();
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const fetchDataList = (showLoading = true) => {
    if (showLoading) {
      loading.tableData = true;
    }

    const payload = {
      Page: pagination.currentPage,
      Limit: pagination.perPage,
      Filters: [{ Seq: 1, Key: "query", Value: "" }],
      Ordering: [{ Seq: 1, Key: "priority", Direction: "desc" }],
      Privilege_Id: formData.privilegeId,
    };

    fetchListData.payload = payload;

    store
      .dispatch("api/apiRequest", fetchListData)
      .then((resp) => {
        const respData = resp.data;

        if (!respData || !respData.is_has_default) {
          formData.critiriaTable = [];
          criteriaDetailItem.isNoDefault = true;
          return openCriteriaListModal();
        }

        criteriaDetailItem.isNoDefault = false;
        formData.critiriaTable = respData
          ? Object.values(respData.payload).sort(
              (a, b) => b.priority - a.priority
            )
          : [];
        pagination.rows = formData.critiriaTable.length;
        pagination.totalRows = respData ? respData.filtered_record : 0;
      })
      .catch((err) => {
        openDefaultErrorModal();
      })
      .finally(() => {
        loading.tableData = false;
        loading.tableField = false;
      });
  };

  const onSubmitForm = () => {
    vm.$emit("onNextStep");
  };

  const checkFormCreateOrEdit = () => {
    if (router.currentRoute.query.privilegeId) {
      vm.$emit(
        "onGetPrivilegeId",
        parseInt(router.currentRoute.query.privilegeId)
      );
    }
  };

  const checkPrivilegePointRef = () => {
    if (localStorage.getItem("pv_PointRef") !== null) {
      formData.pointRef = parseInt(localStorage.getItem("pv_PointRef"));
    }
  };

  onBeforeMount(() => {
    checkFormCreateOrEdit();
    if (props.privilegeId || props.isEdit) {
      formData.privilegeId = props.privilegeId;
      fetchDataList();
      checkPrivilegePointRef();
    }
  });

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchDataList();
      }
    },
    { deep: true }
  );

  return {
    loading,
    formData,
    fields,
    isEditCriteria,
    modalType,
    pagination,
    paginationInfo,
    criteriaDetailItem,
    openCriteriaListModal,
    openDeleteWarningModal,
    closeManageCriteriaListModal,
    saveCriteriaList,
    onSubmitForm,
    resolveDate,
    resolveTime,
    onPageChange,
    onFilterEntity,
  };
}
